import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2, TriangleAlert, X } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { usePode } from '@/stores/authStore';

type SetorDaFolha = { id: number; codigo: string; nome: string };
type CentroCusto = {
  id: number;
  nome: string;
  apelido: string;
  ativo: boolean;
  setores: SetorDaFolha[];
  /** Por que não pode sair do cadastro; nulo quando nada o usa. */
  impede_exclusao: string | null;
};
type Resposta = { data: CentroCusto[]; sem_centro: SetorDaFolha[] };

/**
 * Os centros de custo como o ERP conhece, cada um com os setores da folha que
 * caem nele — "003000000 MEDIÇÃO" em "MEDIÇÃO DE CABOS".
 *
 * É a base de todo mês, feita uma vez: a folha seguinte já chega vinculada.
 * Setor que o escritório criar aparece no topo, esperando o centro de custo.
 */
export default function CentrosCustoPage() {
  const podeConfigurar = usePode('folha.configurar');
  const queryClient = useQueryClient();
  const [novo, setNovo] = useState({ nome: '', apelido: '' });
  const [emEdicao, setEmEdicao] = useState<Record<number, { nome: string; apelido: string }>>({});
  const [excluindo, setExcluindo] = useState<CentroCusto | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['centros-custo'],
    queryFn: async () => (await api.get<Resposta>('/centros-custo')).data,
  });

  // O servidor recalcula as competências abertas a cada mudança aqui.
  const recarregar = () => {
    void queryClient.invalidateQueries({ queryKey: ['centros-custo'] });
    void queryClient.invalidateQueries({ queryKey: ['folha'] });
    void queryClient.invalidateQueries({ queryKey: ['folha-conferencia'] });
    void queryClient.invalidateQueries({ queryKey: ['folha-lancamentos'] });
  };

  const criar = useMutation({
    mutationFn: async () => (await api.post('/centros-custo', novo)).data,
    onSuccess: () => {
      setNovo({ nome: '', apelido: '' });
      recarregar();
    },
  });

  const salvar = useMutation({
    mutationFn: async (centro: CentroCusto) => (await api.put(`/centros-custo/${centro.id}`, emEdicao[centro.id])).data,
    onSuccess: (_, centro) => {
      setEmEdicao((atual) => {
        const proximo = { ...atual };
        delete proximo[centro.id];

        return proximo;
      });
      recarregar();
    },
  });

  const excluir = useMutation({
    mutationFn: async (centro: CentroCusto) => (await api.delete(`/centros-custo/${centro.id}`)).data,
    onSuccess: () => {
      setExcluindo(null);
      recarregar();
    },
  });
  const erroDaExclusao = excluir.error
    ? ((excluir.error as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data?.errors?.centro_custo?.[0] ??
      'Não foi possível excluir o centro de custo.')
    : null;

  const vincular = useMutation({
    mutationFn: async ({ setor, centro }: { setor: number; centro: number | null }) =>
      (await api.put(`/folha/setores/${setor}`, { centro_custo_id: centro })).data,
    onSuccess: recarregar,
  });

  if (isLoading) {
    return <Preloader telaCheia />;
  }

  if (isError || !data) {
    return <p className="px-lg py-lg text-[14px] text-danger">Não foi possível carregar os centros de custo.</p>;
  }

  const centros = data.data;
  const semCentro = data.sem_centro;
  const campo = 'h-8 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink';

  return (
    <div className="max-w-[1200px] px-lg py-lg">
      <header className="mb-lg">
        <h1 className="text-[22px] font-bold text-ink">Centros de custo</h1>
        <p className="mt-xs text-[13px] text-muted">
          O nome tem de ser igual ao do ERP; o apelido vai no histórico do lançamento. Os setores da folha de cada centro
          são vinculados uma vez e valem para as próximas competências.
        </p>
      </header>

      {semCentro.length > 0 && (
        <section className="mb-lg rounded-lg border border-warning/50 bg-warning/5 p-md">
          <h2 className="flex items-center gap-sm text-[14px] font-bold text-ink">
            <TriangleAlert size={16} className="text-warning" />
            {semCentro.length === 1 ? 'Setor da folha sem centro de custo' : `${semCentro.length} setores da folha sem centro de custo`}
          </h2>
          <p className="mt-xs text-[13px] text-muted">A planilha do ERP fica segura até cada um ter destino.</p>

          <ul className="mt-md grid gap-sm md:grid-cols-2">
            {semCentro.map((setor) => (
              <li key={setor.id} className="flex items-center gap-sm">
                <span className="min-w-0 flex-1 truncate text-[13px]">
                  <span className="font-bold text-ink">{setor.nome}</span>
                  <span className="ml-xs text-[11px] text-muted tabular-nums">{setor.codigo}</span>
                </span>
                <select
                  value=""
                  disabled={!podeConfigurar || vincular.isPending}
                  onChange={(evento) => vincular.mutate({ setor: setor.id, centro: Number(evento.target.value) })}
                  className="h-8 w-[260px] rounded-md border border-warning bg-surface px-sm text-[13px] text-ink"
                >
                  <option value="">escolher o centro de custo…</option>
                  {centros.map((centro) => (
                    <option key={centro.id} value={centro.id}>
                      {centro.nome}
                    </option>
                  ))}
                </select>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <table className="w-full text-[13px]">
          <thead className="border-b-2 border-border bg-surface-subtle text-[11px] tracking-wide text-ink-soft uppercase">
            <tr>
              <th className="w-[30%] px-sm py-sm text-left font-bold">Nome no ERP</th>
              <th className="w-[18%] px-sm py-sm text-left font-bold">Apelido no histórico</th>
              <th className="px-sm py-sm text-left font-bold">Setores da folha</th>
              <th className="w-[76px] px-sm py-sm" />
            </tr>
          </thead>
          <tbody>
            {centros.map((centro) => {
              const editado = emEdicao[centro.id];
              const editar = (mudanca: Partial<{ nome: string; apelido: string }>) =>
                setEmEdicao((atual) => ({
                  ...atual,
                  [centro.id]: { ...(atual[centro.id] ?? { nome: centro.nome, apelido: centro.apelido }), ...mudanca },
                }));

              return (
                <tr key={centro.id} className="border-b border-border align-top last:border-0 even:bg-dominant">
                  <td className="px-sm py-xs">
                    <input
                      value={editado?.nome ?? centro.nome}
                      disabled={!podeConfigurar}
                      onChange={(evento) => editar({ nome: evento.target.value })}
                      className={campo}
                    />
                  </td>
                  <td className="px-sm py-xs">
                    <input
                      value={editado?.apelido ?? centro.apelido}
                      disabled={!podeConfigurar}
                      onChange={(evento) => editar({ apelido: evento.target.value })}
                      className={campo}
                    />
                  </td>
                  <td className="px-sm py-xs">
                    {centro.setores.length === 0 ? (
                      <span className="inline-block py-[6px] text-muted">nenhum setor da folha</span>
                    ) : (
                      <ul className="flex flex-wrap gap-xs py-[3px]">
                        {centro.setores.map((setor) => (
                          <li
                            key={setor.id}
                            className="flex items-center gap-xs rounded-full border border-border bg-surface-subtle py-[2px] pr-xs pl-sm"
                          >
                            <span className="text-[11px] text-muted tabular-nums">{setor.codigo}</span>
                            <span className="text-[12px] font-bold text-ink">{setor.nome}</span>
                            {podeConfigurar && (
                              <button
                                type="button"
                                title="Desvincular: o setor volta para a lista dos que faltam"
                                disabled={vincular.isPending}
                                onClick={() => vincular.mutate({ setor: setor.id, centro: null })}
                                className="rounded-full p-[2px] text-muted hover:bg-danger/10 hover:text-danger"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                  <td className="px-sm py-xs text-right whitespace-nowrap">
                    {editado && (
                      <button
                        type="button"
                        onClick={() => salvar.mutate(centro)}
                        title="Salvar"
                        className="rounded-md p-[6px] text-accent hover:bg-accent/10"
                      >
                        <Save size={16} />
                      </button>
                    )}
                    {podeConfigurar && (
                      <button
                        type="button"
                        onClick={() => {
                          excluir.reset();
                          setExcluindo(centro);
                        }}
                        disabled={centro.impede_exclusao !== null}
                        title={centro.impede_exclusao ? `Não dá para excluir: ${centro.impede_exclusao}` : 'Excluir'}
                        className="rounded-md p-[6px] text-muted hover:bg-danger/10 hover:text-danger disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {podeConfigurar && (
        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            criar.mutate();
          }}
          className="mt-md flex flex-wrap items-end gap-sm rounded-lg border border-border bg-surface p-md"
        >
          <label className="min-w-[240px] flex-1">
            <span className="block text-[12px] font-bold text-muted">Nome no ERP</span>
            <input
              value={novo.nome}
              onChange={(evento) => setNovo((atual) => ({ ...atual, nome: evento.target.value }))}
              className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
              required
            />
          </label>
          <label className="w-[200px]">
            <span className="block text-[12px] font-bold text-muted">Apelido</span>
            <input
              value={novo.apelido}
              onChange={(evento) => setNovo((atual) => ({ ...atual, apelido: evento.target.value }))}
              className="mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
              required
            />
          </label>
          <button
            type="submit"
            disabled={criar.isPending}
            className="flex h-9 items-center gap-sm rounded-lg bg-accent px-md text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Plus size={15} />
            Acrescentar
          </button>
        </form>
      )}

      <Dialog open={excluindo !== null} onOpenChange={(aberto) => !excluir.isPending && !aberto && setExcluindo(null)}>
        <DialogContent className="max-w-[460px] p-lg">
          <DialogTitle>Excluir o centro de custo {excluindo?.nome}?</DialogTitle>
          <DialogDescription className="mt-xs">
            Ele não tem setor da folha, não está em nenhuma linha da planilha do ERP e não tem lançamento. Sai do
            cadastro e não dá para desfazer.
          </DialogDescription>
          {erroDaExclusao && <p className="mt-sm text-[13px] font-bold text-danger">{erroDaExclusao}</p>}
          <div className="mt-lg flex justify-end gap-sm">
            <button
              type="button"
              onClick={() => setExcluindo(null)}
              disabled={excluir.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => excluindo && excluir.mutate(excluindo)}
              disabled={excluir.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-danger px-md py-sm text-[13px] font-bold text-white hover:bg-danger/90 disabled:opacity-50"
            >
              <Trash2 size={14} aria-hidden="true" />
              {excluir.isPending ? 'Excluindo…' : 'Excluir centro de custo'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
